package com.sweetlime.dungeonamigo.presentation.features.session

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.cache.Monsters
import com.sweetlime.dungeonamigo.compendium.CompendiumRepository
import com.sweetlime.dungeonamigo.model.CharacterCalculator
import com.sweetlime.dungeonamigo.model.DmEntity
import com.sweetlime.dungeonamigo.model.DmEntitySource
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import org.koin.compose.koinInject

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddDmEntitySheet(vm: SessionViewModel, onDismiss: () -> Unit) {
    val compendiumRepo: CompendiumRepository = koinInject()
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    var selectedTab by remember { mutableStateOf(0) }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = BrandColors.Surface,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding(),
        ) {
            // ── Tab Row ───────────────────────────────────────────────────────
            TabRow(
                selectedTabIndex = selectedTab,
                containerColor = BrandColors.Surface,
                contentColor = BrandColors.Parchment,
                indicator = { tabPositions ->
                    if (selectedTab < tabPositions.size) {
                        Box(
                            modifier = Modifier
                                .tabIndicatorOffset(tabPositions[selectedTab])
                                .height(2.dp)
                                .background(BrandColors.Parchment),
                        )
                    }
                },
            ) {
                listOf("From Compendium", "Homebrew").forEachIndexed { index, label ->
                    Tab(
                        selected = selectedTab == index,
                        onClick = { selectedTab = index },
                        selectedContentColor = BrandColors.Parchment,
                        unselectedContentColor = BrandColors.TextSecondary,
                    ) {
                        Text(
                            label,
                            modifier = Modifier.padding(vertical = 12.dp),
                            fontSize = 13.sp,
                            fontWeight = if (selectedTab == index) FontWeight.SemiBold else FontWeight.Normal,
                        )
                    }
                }
            }

            when (selectedTab) {
                0 -> CompendiumTab(repo = compendiumRepo, vm = vm, onDismiss = onDismiss)
                1 -> HomebrewTab(vm = vm, onDismiss = onDismiss)
            }
        }
    }
}

// ── Compendium Tab ─────────────────────────────────────────────────────────────

@Composable
private fun CompendiumTab(
    repo: CompendiumRepository,
    vm: SessionViewModel,
    onDismiss: () -> Unit,
) {
    var query by remember { mutableStateOf("") }
    val monsters = remember(query) { repo.searchMonsters(query) }
    var selectedMonster by remember { mutableStateOf<Monsters?>(null) }
    var instanceCount by remember { mutableStateOf(1) }

    Column(modifier = Modifier.fillMaxWidth()) {
        // Search bar
        OutlinedTextField(
            value = query,
            onValueChange = { query = it },
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            placeholder = { Text("Search monsters...", color = BrandColors.TextSecondary, fontSize = 13.sp) },
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = BrandColors.Parchment,
                unfocusedBorderColor = BrandColors.SurfaceLight,
                focusedTextColor = BrandColors.TextPrimary,
                unfocusedTextColor = BrandColors.TextPrimary,
                cursorColor = BrandColors.Parchment,
            ),
            shape = RoundedCornerShape(8.dp),
            singleLine = true,
        )

        LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .height(360.dp),
            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 4.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            items(monsters) { monster ->
                MonsterSearchRow(monster = monster) {
                    selectedMonster = monster
                    instanceCount = 1
                }
            }

            if (monsters.isEmpty()) {
                item {
                    Text(
                        "No monsters found",
                        color = BrandColors.TextSecondary,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(vertical = 16.dp),
                    )
                }
            }
        }
    }

    // Instance picker dialog
    selectedMonster?.let { monster ->
        AlertDialog(
            onDismissRequest = { selectedMonster = null },
            containerColor = BrandColors.Surface,
            title = {
                Text(
                    monster.name,
                    color = BrandColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                )
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("How many?", color = BrandColors.TextSecondary, fontSize = 13.sp)
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        (1..6).forEach { n ->
                            val selected = n == instanceCount
                            Box(
                                modifier = Modifier
                                    .size(40.dp)
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(if (selected) BrandColors.Parchment else BrandColors.SurfaceLight)
                                    .clickable { instanceCount = n },
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    "$n",
                                    color = if (selected) BrandColors.Background else BrandColors.TextPrimary,
                                    fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                                    fontSize = 14.sp,
                                )
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        vm.addEntityFromMonster(monster, instanceCount)
                        selectedMonster = null
                        onDismiss()
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                    shape = RoundedCornerShape(8.dp),
                ) {
                    Text("Add to Session", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { selectedMonster = null }) {
                    Text("Cancel", color = BrandColors.TextSecondary)
                }
            },
        )
    }
}

@Composable
private fun MonsterSearchRow(monster: Monsters, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(BrandColors.SurfaceLight)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Column(Modifier.weight(1f)) {
            Text(monster.name, color = BrandColors.TextPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            if (!monster.type.isNullOrBlank()) {
                Text(monster.type, color = BrandColors.TextSecondary, fontSize = 11.sp)
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            if (!monster.hit_points.isNullOrZero()) {
                Text("${monster.hit_points} HP", color = BrandColors.TextSecondary, fontSize = 11.sp)
            }
            val cr = monster.challenge_rating
            if (!cr.isNullOrBlank()) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(6.dp))
                        .background(crBadgeColor(cr).copy(alpha = 0.15f))
                        .padding(horizontal = 6.dp, vertical = 2.dp),
                ) {
                    Text("CR $cr", color = crBadgeColor(cr), fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

private fun Long?.isNullOrZero() = this == null || this == 0L

private fun crBadgeColor(cr: String): Color {
    val value = parseCr(cr)
    return when {
        value >= 10 -> BrandColors.Ember
        value >= 1 -> BrandColors.Parchment
        else -> BrandColors.TextSecondary
    }
}

private fun parseCr(cr: String): Double {
    if (cr.isBlank()) return 0.0
    if (cr.contains('/')) {
        val parts = cr.split('/')
        val num = parts.getOrNull(0)?.toDoubleOrNull() ?: return 0.0
        val den = parts.getOrNull(1)?.toDoubleOrNull()?.takeIf { it != 0.0 } ?: 1.0
        return num / den
    }
    return cr.toDoubleOrNull() ?: 0.0
}

// ── Homebrew Tab ───────────────────────────────────────────────────────────────

@Composable
private fun HomebrewTab(vm: SessionViewModel, onDismiss: () -> Unit) {
    var showUpgradeDialog by remember { mutableStateOf(false) }

    // Form state
    var name by remember { mutableStateOf("") }
    var maxHp by remember { mutableStateOf("") }
    var ac by remember { mutableStateOf("10") }
    var speed by remember { mutableStateOf("30") }
    var cr by remember { mutableStateOf("") }
    var type by remember { mutableStateOf("") }
    var str by remember { mutableStateOf("10") }
    var dex by remember { mutableStateOf("10") }
    var con by remember { mutableStateOf("10") }
    var int_ by remember { mutableStateOf("10") }
    var wis by remember { mutableStateOf("10") }
    var cha by remember { mutableStateOf("10") }
    var notes by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // Amigo+ locked card — TODO Amigo+: implement saved NPC templates
        SavedNpcLockedCard(onClick = { showUpgradeDialog = true })

        // Name
        val nameError = submitted && name.isBlank()
        OutlinedTextField(
            value = name,
            onValueChange = { name = it },
            label = { Text("Name *", color = BrandColors.TextSecondary, fontSize = 12.sp) },
            modifier = Modifier.fillMaxWidth(),
            colors = entityFieldColors(),
            shape = RoundedCornerShape(8.dp),
            singleLine = true,
            isError = nameError,
            supportingText = if (nameError) {
                { Text("Name is required", color = BrandColors.Error, fontSize = 11.sp) }
            } else null,
        )

        // Max HP + AC
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            val hpError = submitted && maxHp.toIntOrNull() == null
            OutlinedTextField(
                value = maxHp,
                onValueChange = { maxHp = it.filter { c -> c.isDigit() } },
                label = { Text("Max HP *", color = BrandColors.TextSecondary, fontSize = 12.sp) },
                modifier = Modifier.weight(1f),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                colors = entityFieldColors(),
                shape = RoundedCornerShape(8.dp),
                singleLine = true,
                isError = hpError,
                supportingText = if (hpError) {
                    { Text("Required", color = BrandColors.Error, fontSize = 11.sp) }
                } else null,
            )
            OutlinedTextField(
                value = ac,
                onValueChange = { ac = it.filter { c -> c.isDigit() }.take(3) },
                label = { Text("AC", color = BrandColors.TextSecondary, fontSize = 12.sp) },
                modifier = Modifier.weight(1f),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                colors = entityFieldColors(),
                shape = RoundedCornerShape(8.dp),
                singleLine = true,
            )
        }

        // Speed + CR
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(
                value = speed,
                onValueChange = { speed = it.filter { c -> c.isDigit() }.take(3) },
                label = { Text("Speed", color = BrandColors.TextSecondary, fontSize = 12.sp) },
                modifier = Modifier.weight(1f),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                colors = entityFieldColors(),
                shape = RoundedCornerShape(8.dp),
                singleLine = true,
            )
            OutlinedTextField(
                value = cr,
                onValueChange = { cr = it.take(6) },
                label = { Text("CR", color = BrandColors.TextSecondary, fontSize = 12.sp) },
                modifier = Modifier.weight(1f),
                colors = entityFieldColors(),
                shape = RoundedCornerShape(8.dp),
                singleLine = true,
            )
        }

        // Type
        OutlinedTextField(
            value = type,
            onValueChange = { type = it },
            label = { Text("Type", color = BrandColors.TextSecondary, fontSize = 12.sp) },
            placeholder = { Text("e.g. humanoid, beast", color = BrandColors.TextSecondary, fontSize = 12.sp) },
            modifier = Modifier.fillMaxWidth(),
            colors = entityFieldColors(),
            shape = RoundedCornerShape(8.dp),
            singleLine = true,
        )

        // Ability scores
        Text("Ability Scores", color = BrandColors.TextSecondary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                AbilityScoreField("STR", str, Modifier.weight(1f)) { str = it }
                AbilityScoreField("DEX", dex, Modifier.weight(1f)) { dex = it }
                AbilityScoreField("CON", con, Modifier.weight(1f)) { con = it }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                AbilityScoreField("INT", int_, Modifier.weight(1f)) { int_ = it }
                AbilityScoreField("WIS", wis, Modifier.weight(1f)) { wis = it }
                AbilityScoreField("CHA", cha, Modifier.weight(1f)) { cha = it }
            }
        }

        // Notes
        OutlinedTextField(
            value = notes,
            onValueChange = { notes = it },
            label = { Text("DM Notes", color = BrandColors.TextSecondary, fontSize = 12.sp) },
            placeholder = { Text("Private notes...", color = BrandColors.TextSecondary, fontSize = 12.sp) },
            modifier = Modifier.fillMaxWidth(),
            colors = entityFieldColors(),
            shape = RoundedCornerShape(8.dp),
            minLines = 2,
            maxLines = 4,
        )

        // Add button
        Button(
            onClick = {
                submitted = true
                val hp = maxHp.toIntOrNull() ?: return@Button
                if (name.isBlank()) return@Button
                val dexVal = dex.toIntOrNull() ?: 10
                val entity = DmEntity(
                    name = name.trim(),
                    sourceType = DmEntitySource.HOMEBREW,
                    maxHp = hp,
                    currentHp = hp,
                    armorClass = ac.toIntOrNull() ?: 10,
                    speed = speed.toIntOrNull() ?: 30,
                    challengeRating = cr.trim(),
                    type = type.trim(),
                    strength = str.toIntOrNull() ?: 10,
                    dexterity = dexVal,
                    constitution = con.toIntOrNull() ?: 10,
                    intelligence = int_.toIntOrNull() ?: 10,
                    wisdom = wis.toIntOrNull() ?: 10,
                    charisma = cha.toIntOrNull() ?: 10,
                    initiative = CharacterCalculator.modifier(dexVal),
                    notes = notes.trim(),
                )
                vm.addHomebrewEntity(entity)
                onDismiss()
            },
            modifier = Modifier.fillMaxWidth(),
            colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
            shape = RoundedCornerShape(8.dp),
        ) {
            Text("Add to Session", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 14.sp)
        }

        Spacer(Modifier.height(8.dp))
    }

    // Amigo+ upgrade prompt
    if (showUpgradeDialog) {
        AlertDialog(
            onDismissRequest = { showUpgradeDialog = false },
            containerColor = BrandColors.Surface,
            title = {
                Text("Amigo+ Feature", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold)
            },
            text = {
                Text(
                    "Save your homebrew NPCs for reuse across sessions with Amigo+.",
                    color = BrandColors.TextSecondary,
                    fontSize = 13.sp,
                )
            },
            confirmButton = {
                TextButton(onClick = { showUpgradeDialog = false }) {
                    Text("Got it", color = BrandColors.Parchment, fontWeight = FontWeight.Bold)
                }
            },
        )
    }
}

@Composable
private fun SavedNpcLockedCard(onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(BrandColors.SurfaceLight)
            .border(1.dp, BrandColors.SpellBlue.copy(alpha = 0.3f), RoundedCornerShape(10.dp))
            .clickable(onClick = onClick)
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(Icons.Filled.Lock, contentDescription = null, tint = BrandColors.TextSecondary, modifier = Modifier.size(20.dp))
        Column(Modifier.weight(1f)) {
            Text("Saved NPCs", color = BrandColors.TextPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            Text(
                "Save your homebrew NPCs for reuse across sessions",
                color = BrandColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .background(BrandColors.SpellBlue.copy(alpha = 0.2f))
                .padding(horizontal = 8.dp, vertical = 4.dp),
        ) {
            Text("Amigo+", color = BrandColors.SpellBlue, fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
        }
    }
}

@Composable
private fun AbilityScoreField(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    onChange: (String) -> Unit,
) {
    val score = value.toIntOrNull() ?: 10
    val mod = CharacterCalculator.modifier(score)
    val modStr = if (mod >= 0) "+$mod" else "$mod"

    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(label, color = BrandColors.TextSecondary, fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
        OutlinedTextField(
            value = value,
            onValueChange = { v -> onChange(v.filter { it.isDigit() }.take(2)) },
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            colors = entityFieldColors(),
            shape = RoundedCornerShape(8.dp),
            singleLine = true,
        )
        Text(modStr, color = BrandColors.TextSecondary, fontSize = 10.sp)
    }
}

@Composable
private fun entityFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = BrandColors.Parchment,
    unfocusedBorderColor = BrandColors.SurfaceLight,
    focusedTextColor = BrandColors.TextPrimary,
    unfocusedTextColor = BrandColors.TextPrimary,
    cursorColor = BrandColors.Parchment,
    focusedContainerColor = BrandColors.Surface,
    unfocusedContainerColor = BrandColors.Surface,
)
